import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { PropertyContactInfo } from './PropertyContactInfo';
import type { OfficeHourEntry } from './PropertyContactInfo';
import { ctaArrowCss, ctaHoverCss } from '../utils/ctaHover';

const PREFIX = 'scheduleatour';

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

const addressSlotDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text:    { label: 'Address Text', type: 'text', defaultField: 'property_full_address' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (PropertyContactInfo). Editor overrides still win.
    url:     { label: 'Map URL',      type: 'url' },
    _target: { label: 'Target',       type: 'text', defaultValue: '_blank' },
  },
};

export function ScheduleATour() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const formSlot = useBlockSlot(`${PREFIX}.form_block`, { defaultValue: 'multihub-tour-scheduler' });

  const visitLabel = useContentToken(`${PREFIX}.visit_label`);
  const callLabel  = useContentToken(`${PREFIX}.call_label`);
  const hoursLabel = useContentToken(`${PREFIX}.hours_label`);
  const addressSlot = useSlotGroup(`${PREFIX}.address`, addressSlotDef);
  const phoneToken = useContentToken(`${PREFIX}.phone`);
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;

  const officeHours = useCollectionBinding<OfficeHourEntry>(`${PREFIX}.office_hours`, {
    collection: 'office_hours',
  });

  return (
    <section ref={sectionRef} className="reveal-fade-up sat-section">
      <div className="sat-container">
        <div className="sat-grid">
          <div className="sat-info-col">
            <PropertyContactInfo
              prefix={PREFIX}
              visitLabel={visitLabel}
              callLabel={callLabel}
              hoursLabel={hoursLabel}
              address={addressSlot.text}
              addressUrl={addressSlot.url}
              addressTarget={addressSlot._target}
              phone={phone}
              officeHours={officeHours}
              addressGroupPath={`${PREFIX}.address`}
              addressGroupDef={addressSlotDef}
            />
          </div>

          <div className="sat-form-col">
            <EditableToken path="scheduleatour.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : null}
            </EditableToken>
          </div>
        </div>
      </div>

      <style>{`
        .sat-section {
          padding: 2.5rem 0 3.5rem;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .sat-section { padding: 3rem 0 4rem; }
        }
        .sat-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sat-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sat-container { padding: 0 2rem; } }

        .sat-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .sat-grid {
            grid-template-columns: 5fr 7fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        .sat-info-col :global(.pci-block-section) { padding: 0; }
        .sat-info-col :global(.pci-block-container) { padding: 0; max-width: none; }

        .sat-form-col { position: relative; }

        /* Tour scheduler CTAs (Continue / Schedule Tour, and the fallback contact link) */
        ${ctaHoverCss([
          '.sat-form-col button.ml-auto.bg-primary',
          '.sat-form-col a.inline-block.bg-primary',
        ])}
        ${ctaArrowCss([
          '.sat-form-col button.ml-auto.bg-primary',
          '.sat-form-col a.inline-block.bg-primary',
        ])}
      `}</style>

      {/* Schema-lint anchors — PropertyContactInfo uses dynamic `${prefix}.*` paths
          on its EditableTokens/EditableSlotGroups, so these hidden elements satisfy
          token-missing-editable-wrapper / slot-group-missing-editable-wrapper. */}
      <EditableToken path="scheduleatour.visit_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.call_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.phone"       as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.hours_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      {/* Slot-group anchor — satisfies slot-group-missing-editable-wrapper for the dynamic prefix path in PropertyContactInfo */}
      <div style={{ display: 'none' }}>
        <EditableSlotGroup groupPath="scheduleatour.address" definition={addressSlotDef}>
          <span />
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleATour',
  displayName: 'Schedule A Tour',
  tokens: {
    'scheduleatour.form_block': {
      label: 'Tour Scheduler Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduleatour.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduleatour.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduleatour.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
    'scheduleatour.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduleatour.address': addressSlotDef,
  },
  collectionBindings: {
    'scheduleatour.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleATour as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleATour;
